import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import HiddenFieldsLazy from '../../molecules/hiddenFields/hiddenFieldsLazy';
import AddDates from '../../molecules/addDates/addDates';
import SearchSecondaryBarLazy from '../../molecules/searchSecondaryBar/searchSecondaryBarLazy';
import Destination from '../../molecules/destination/destination';
import FindBtn from '../../atoms/findBtn/findBtn';
import { SearchDesktopPrimary } from './searchDesktop.styles';
import { showConfirmOnFormSubmit } from '../../../utils/Helpers';
import { ROOMS_AND_GUESTS } from '../../../constants/StoreConstants';
import config from '../../../config';
import { MAX_ROOM_SELECTION_LIMIT } from '../../../constants/ApplicationConstants';
import { StyledSearchContainer } from '../../index.styles';
import clsx from 'clsx';
import RoomsAndGuests from '../../molecules/roomsAndGuests';
import { useConditionalPageContextSERP } from '../../../../../../hooks';
import { ContentContext, FocusContext, FormValuesContext } from '../../index.contexts';
import { MAP_VIEW_SESSION_KEY } from '../../../../../../modules/store/store.constants';
import { getSearchFormClickTrackingLoc } from '../../../../../../modules/utils/searchFormHelper';

const showSecondary = false;

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface HTMLAttributes<T> {
    custom_click_track_value?: string;
  }
}

const SearchDesktop: FC<{
  className?: string;
  isHideDestinationProps?: boolean;
  isPublishUserSelection?: boolean;
  isHideFlexibleDates?: boolean;
  isSearchPage?: boolean;
  enableTwoSeparateButtonsForDates?: boolean;
  sessionToken?: string;
  isOnLoad: boolean;
  isScrolled: boolean;
  // eslint-disable-next-line
  searchContainerRef: any;
  isADFPage?: boolean;
  setSearchLoadForm: () => void;
  isBulgariDomain?: boolean;
}> = ({
  className,
  isHideDestinationProps = false,
  isPublishUserSelection = false,
  isHideFlexibleDates = false,
  isSearchPage = false,
  enableTwoSeparateButtonsForDates = false,
  sessionToken,
  isOnLoad,
  isScrolled,
  searchContainerRef,
  isADFPage = false,
  setSearchLoadForm,
  isBulgariDomain = false,
}) => {
  const { findHotelsRemoteSPA } = useConditionalPageContextSERP();
  const formRef = useRef(null);
  const { formValues } = useContext(FormValuesContext);
  const { isUserInteractWithComp } = useContext(FocusContext);
  const { isItForOffersPage, usePointsConfirmMessage, collapseOnPageLoad, collapseWhenUserUpdates, hideDestination } =
    useContext(ContentContext);
  const { roomsCount = '1' } = formValues?.[ROOMS_AND_GUESTS] ?? {};
  const { searchFormGroupActionURL, offerSearchFormDefaultActionURL, searchFormNODefaultAction } = config;

  // Don't render hidden fields on Server sie rendering.
  const [showLazyLoadedComponents, setShowLazyLoadedComponents] = useState(false);

  const usePointsValue = formValues?.usePoints?.value;

  const { flexible } = formValues?.dates ?? {};

  const timestamp = new Date().getTime();
  const desktopFormTopPadding = 12;

  useEffect(() => {
    // On Client side, render the hidden fields
    setSearchLoadForm();
    setShowLazyLoadedComponents(true);
  }, []);

  return (
    <>
      {showLazyLoadedComponents && (
        <StyledSearchContainer
          desktopFormTopPadding={desktopFormTopPadding}
          className={clsx(
            'search__container',
            hideDestination ? 'search__container-hide-destination' : '',
            'search-form-shop-page-container custom-scrollbar m-container-fullbleed ',
            isADFPage ? 'adf-search-form-container m-container-fullbleed ' : '',
            !isADFPage ? 'pt-3' : '',
            'standard'
          )}
          ref={searchContainerRef}
          id="document_search_form_container_el_ref" // used in another fragment to traverse on document node to get the element ref.
          tabIndex={-1}
        >
          <form
            ref={formRef}
            action={
              isItForOffersPage
                ? offerSearchFormDefaultActionURL
                : roomsCount === MAX_ROOM_SELECTION_LIMIT
                ? searchFormGroupActionURL
                : searchFormNODefaultAction
            }
            id={timestamp.toString() + '-find-a-hotel-homePage-form'}
            noValidate
            method="get"
            autoComplete="off"
            className={clsx('container', 'm-hide-mobile', className, isADFPage ? 'adf-form-wrapper' : '')}
            role="search"
            onSubmit={(event: React.SyntheticEvent): void =>
              showConfirmOnFormSubmit(event, roomsCount, usePointsValue, usePointsConfirmMessage, flexible)
            }
          >
            {showLazyLoadedComponents ? <HiddenFieldsLazy isPublishUserSelection={isPublishUserSelection} /> : ''}
            <div
              className={clsx(
                'search-primary-container',
                isSearchPage && 'shop-page-search-container',
                isADFPage && 'adf-search-container-tablet',
                findHotelsRemoteSPA && 'shop-page-search-container-spa',
                typeof sessionStorage !== 'undefined' &&
                  sessionStorage.getItem(MAP_VIEW_SESSION_KEY) === 'true' &&
                  'm-container-fullbleed'
              )}
            >
              <div className="container" id="search_desktop_container_ref">
                <SearchDesktopPrimary
                  className={clsx(
                    'row',
                    'search-primary',
                    isSearchPage ? 'shop-page-search-primary' : '',
                    (isOnLoad && !collapseOnPageLoad && !isScrolled) ||
                      (!isScrolled && !collapseWhenUserUpdates && !isOnLoad) ||
                      (showSecondary && !isItForOffersPage && showLazyLoadedComponents) ||
                      (isSearchPage && isUserInteractWithComp)
                      ? 'br-half'
                      : 'br-full',
                    isADFPage && 'br-0'
                  )}
                >
                  {!isHideDestinationProps && (
                    <div
                      className={`col-md-4 ${isSearchPage && 'shop-destination-wrapper'} search-primary-destination ${
                        enableTwoSeparateButtonsForDates && 'shop-page-split-date-box-destination'
                      } custom_click_track`}
                      custom_click_track_value={`${getSearchFormClickTrackingLoc()}| Destination Field |internal`}
                    >
                      <Destination isSearchPage={isSearchPage} />
                    </div>
                  )}
                  <div
                    className={clsx(
                      `col-md-6 ${isADFPage ? 'adf-search-desktop' : ''}`,
                      `${isSearchPage && 'shop-dates-wrapper'}`,
                      `${enableTwoSeparateButtonsForDates && 'shop-page-split-date-box-container'}`
                    )}
                  >
                    <AddDates
                      isHideFlexibleDates={isHideFlexibleDates}
                      isSearchPage={isSearchPage}
                      enableTwoSeparateButtonsForDates={enableTwoSeparateButtonsForDates}
                      isADFPage={isADFPage}
                    />
                  </div>
                  {/* flag added to display only incase of ADF Page */}
                  {isADFPage && (
                    <RoomsAndGuests
                      isPublishUserSelection={isPublishUserSelection}
                      isSearchPage={isSearchPage || isADFPage}
                      isADFPage={isADFPage}
                    />
                  )}
                  <FindBtn
                    isPublishUserSelection={isPublishUserSelection}
                    isSearchPage={isSearchPage}
                    sessionToken={sessionToken}
                    searchFormRef={formRef}
                    tabIndex={0}
                    isADFPage={isADFPage}
                  />
                </SearchDesktopPrimary>
              </div>
            </div>
            {/* Refactored the condition to consume all flags at once  to display and hide the component instead of referring it twice in html */}
            {(isUserInteractWithComp ||
              (isOnLoad && !collapseOnPageLoad && !isScrolled) ||
              (!collapseWhenUserUpdates && !isOnLoad && !isScrolled)) &&
              ((isADFPage && !isBulgariDomain) || !isADFPage) && (
                <SearchSecondaryBarLazy
                  isPublishUserSelection={isPublishUserSelection}
                  isSearchPage={isSearchPage}
                  isADFPage={isADFPage}
                />
              )}
          </form>
        </StyledSearchContainer>
      )}
    </>
  );
};

export default SearchDesktop;
